import React, { useContext, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { isEqual } from 'lodash';
import { SButton } from '@chargebee/sting-react';
import { NavIcons } from '@bbkAdminComponents/app-layout/nav';
import { FunnelPlacementContext } from '@bbkAdminComponents/experiences/offers/funnel-placement-context';
import {
  selectAppId,
  selectCompanyInternalName,
} from '@bbkAdminRedux/app/selectors';
import { experienceSlice } from '@bbkAdminRedux/rtkq/experience.slice';
import { useSelectFunnelsLoading } from '@bbkAdminRedux/rtkq/funnels.slice';
import {
  placementsSlice,
  useSelectPlacementsByFunnelId,
} from '@bbkAdminRedux/rtkq/placements.slice';
import {
  type PlacementLossAversionPosition,
  type RestFunnelPlacementLossAversion,
} from '@bbkAdminRedux/rtkq/placements.utils';
import { PublishState } from '@bbkAdminUtils/api-client/rest-offers-api';
import { FeatureFlagsE, useFeatureFlags } from '@bbkAdminUtils/feature-flags';
import { type ReactFCC } from '@bbkAdminUtils/utility-types';
import icons from '@bbkFunnel/utils/icons';
import { FunnelContext } from '@bbkFunnelComponents/FunnelContext';
import { LossAversionCardSelector } from '@bbkFunnelComponents/loss-aversion-card-selector';

type Props = {
  slot: PlacementLossAversionPosition;
  hasError: boolean;
};

const AddCardButtonContainer = styled.div`
  margin-top: 20px;
`;

const ErrorContainer = styled.div`
  padding-top: 20px;
  color: red;
  font-size: 14px;
  font-weight: bold;
`;

const CardSelectorContainer = styled.div`
  width: 371px;
  margin-top: 20px;
`;
const ContentContainer = styled.div`
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;
const InnerContainer = styled.div`
  border: 1px dashed gray;
  height: 100%;

  display: flex;
  justify-content: center;
  align-items: center;
`;
const OuterContainer = styled.div`
  height: 100%;
  width: 100%;
  border: solid 10px #f4f5f5;
`;

export const EmptyLACard: ReactFCC<Props> = ({ slot, hasError }) => {
  const companyKey = useSelector(selectCompanyInternalName);
  const appKey = useSelector(selectAppId);
  const showLaLayoutControls = useFeatureFlags(
    FeatureFlagsE.SHOW_LA_LAYOUT_CONTROLS
  );
  const { funnelProps } = useContext(FunnelContext);
  const funnelId = +funnelProps!.funnelId;
  const origFunnelPlacement = useSelectPlacementsByFunnelId(
    { appKey },
    funnelId
  );
  const [savePlacement, { isLoading: isSavingPlacement }] =
    placementsSlice.endpoints.updatePlacement.useMutation();
  const { isFetching: isFetchingFunnels } = useSelectFunnelsLoading({ appKey });
  const { isFetching: isFetchingExperience } =
    experienceSlice.endpoints.getExperience.useQuery({
      companyKey,
      appKey,
      funnelId,
      last_draft: true,
    });
  const { isFetching: isFetchingPlacements } =
    placementsSlice.endpoints.getPlacements.useQuery({
      appKey,
      publishState: PublishState.draft,
    });

  const isLoading =
    isFetchingFunnels ||
    isFetchingExperience ||
    isFetchingPlacements ||
    isSavingPlacement;

  const { funnelPlacement, setFunnelPlacement } = useContext(
    FunnelPlacementContext
  );
  useEffect(() => {
    if (!origFunnelPlacement) return;
    setFunnelPlacement(origFunnelPlacement);
  }, [origFunnelPlacement?.placements]);

  const handleSave = async () => {
    if (!funnelPlacement || isEqual(funnelPlacement, origFunnelPlacement)) {
      return;
    }
    await savePlacement({
      appKey,
      funnelId,
      placements: funnelPlacement.placements,
      publish_state: PublishState.draft,
      comment: 'Updated loss aversion placement from Page editor',
    });
  };

  const isNoneSelected = useMemo(() => {
    const selectedSlotPlacement = funnelPlacement?.placements.find(
      (p) => p.position === slot
    ) as RestFunnelPlacementLossAversion;
    return !selectedSlotPlacement?.content_id;
  }, [funnelPlacement, slot]);

  return (
    <OuterContainer>
      <InnerContainer>
        <ContentContainer>
          {NavIcons.addLaCard}
          <CardSelectorContainer>
            <LossAversionCardSelector slot={slot} />
          </CardSelectorContainer>
          <AddCardButtonContainer>
            <SButton
              variant="primary"
              disabled={
                isFetchingFunnels ||
                isFetchingExperience ||
                isFetchingPlacements ||
                isSavingPlacement ||
                isNoneSelected
              }
              onClick={handleSave}
            >
              {isLoading && (
                <span className="bbk-fde-loader-spinner">
                  {icons.loaderSW6}
                </span>
              )}
              Add Card
            </SButton>
          </AddCardButtonContainer>
          {hasError && (
            <ErrorContainer>
              {`You cannot publish this experience with an empty loss aversion card slot. Please select an LA card to fill this empty slot${
                showLaLayoutControls
                  ? ', or modify the loss aversion layout from the Page Settings menu.'
                  : '.'
              }`}
            </ErrorContainer>
          )}
        </ContentContainer>
      </InnerContainer>
    </OuterContainer>
  );
};
